import { CommonModule } from '@angular/common';
import { Component, computed, Input, output, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthForm } from '../../types/auth-form.type';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateService, _ } from '@ngx-translate/core';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { ForgotPasswordRequestComponent } from '../../pages/forgot-password-request/forgot-password-request.component';
import { UpdatePasswordComponent } from '../../pages/update-password/update-password.component';
@Component({
  selector: 'app-signin-form',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    InputFieldComponent,
    CtaButtonComponent,
    ParentButtonComponent,
    ForgotPasswordRequestComponent,
    UpdatePasswordComponent,
    // SocialSigningsComponent,
  ],
  templateUrl: './signin-form.component.html',
  styleUrls: ['./signin-form.component.scss'],
})
export class SigninFormComponent {
  #requestPending = signal(false);
  #layout = signal<string | undefined>(undefined);
  #mode = signal<'signin' | 'forgot' | 'update'>('signin');
  #resetUserName = signal<string | null>(null);

  private takeUntilDestroyed = getTakeUntilDestroyed();

  public signinForm!: FormGroup<AuthForm>;
  public nameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });
  public passwordValidation = signal<Record<string, string>>({
    required: '',
  });
  public userNameValidation = signal<Record<string, string>>({
    required: '',
    userName: '',
  });

  public formSubmitted = output<AuthForm>();

  readonly isLoading = computed(() => this.#requestPending());
  readonly layout = computed(() => this.#layout());
  readonly isParentLayout = computed(
    () => this.layout() === 'parent' || this.layout() === 'admin',
  );
  readonly mode = computed(() => this.#mode());
  readonly resetUserName = computed(() => this.#resetUserName());

  @Input() set requestPending(v: boolean) {
    this.#requestPending.set(v);
  }

  @Input({ alias: 'layout' }) set layoutValue(value: string | undefined) {
    this.#layout.set(value);
  }

  constructor(private translate: TranslateService, private fb: FormBuilder) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadValidationMessages();
  }

  public onSubmit(): void {
    if (this.signinForm.valid) {
      this.formSubmitted.emit(this.signinForm.getRawValue());
    }
  }

  public onForgotPasswordClick(): void {
    this.#mode.set('forgot');
  }

  public onResetRequested(userName: string): void {
    this.#resetUserName.set(userName);
    this.#mode.set('update');
  }

  public onResetBack(): void {
    this.#mode.set('signin');
  }

  public onPasswordUpdated(): void {
    this.#mode.set('signin');
    this.#resetUserName.set(null);
  }

  private initForm(): void {
    this.signinForm = this.fb.group<AuthForm>({
      userName: this.fb.control('', {
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-z0-9._]+$/),
        ],
        nonNullable: true,
      }),
      password: this.fb.control('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }

  private loadValidationMessages(): void {
    this.translate
      .get([
        'app.auth.requiredUserName',
        'app.auth.validUserName',
        'app.auth.requiredPassword',
      ])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.nameValidation.set({
          required: translations['app.auth.requiredEmail'],
          userName: translations['app.auth.validEmail'],
        });
        this.passwordValidation.set({
          required: translations['app.auth.requiredPassword'],
        });
        this.userNameValidation.set({
          required: translations['app.auth.requiredUserName'],
          userName: translations['app.auth.validUserName'],
        });
      });
  }
}
